// src/pages/SalesReturnRecord/components/exportSalesReturnRecordCsv.js
import { formatToCompanyTimezone, numberFormatTax } from "../../../lib/functions";

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const money = (v) => Number(v || 0).toFixed(2);
const fmtDate = (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-");
const byItemFamily = ["By Item", "By Group", "By Category"];

/**
 * @param {object} params
 * @param {Array}   params.list      - rows from Redux state.salesReturnRecord.list
 * @param {object}  params.filters   - filterType + recordType
 * @param {object}  params.authInfo  - for taxLabel + GST columns
 * @param {string}  params.title
 * @param {string}  params.filename
 */
export const exportSalesReturnRecordCsv = ({
  list,
  filters,
  authInfo,
  title    = "Sales Return Record",
  filename = "sales_return_record",
}) => {
  const isIndia      = authInfo?.country?.toLowerCase() === "india";
  const isGstEnabled = authInfo?.gst_enabled === "yes";
  const showGst      = isIndia && isGstEnabled;
  const taxLabel     = isIndia ? "GST" : "VAT";
  const { filterType, recordType } = filters;
  const isItemBased  = byItemFamily.includes(filterType);

  // ── Choose headers + row builder ──────────────────────────────
  let headers = [];
  let buildRow;
  let buildTotalRow;

  if (recordType === "with item") {
    headers = [
      "No.", "VCH No", "Date", "Customer/Debtor", "Employee",
      "Created By", "Item", "Rate", "Discount%", "VAT%", "Qty", "Total",
    ];
    buildRow = (row, i) => [
      i + 1,
      row.vch_no    || "",
      fmtDate(row.date),
      row.customer  || "",
      row.employee  || "",
      row.createdBy || "",
      row.item      || "",
      money(row.rate),
      money(row.discount),
      money(row.vat),
      row.qty,
      money(row.total),
    ];

    buildTotalRow = () => {
      const masterQty = list.reduce((s, r) => s + Number(r.qty    || 0), 0);
      const baseQty   = list.reduce((s, r) => s + Number(r.baseQty|| 0), 0);
      const total     = list.reduce((s, r) => s + Number(r.total  || 0), 0);
      const row       = Array(headers.length).fill("");
      row[0]  = "Grand Total:";
      row[9]  = `Master Qty: ${money(masterQty)} / Base Qty: ${money(baseQty)}`;
      row[11] = money(total);
      return row;
    };

  } else if (isItemBased) {
    headers = [
      "No.", "Voucher No", "Date", "Customer/Debtor", "Created By",
      "Item", "Rate", "Qty", "Group", "Category",
      "Discount%", "VAT%", "Total",
    ];
    buildRow = (row, i) => [
      i + 1,
      row.vch_no        || "",
      fmtDate(row.date),
      row.customer      || "",
      row.createdBy     || "",
      row.item          || "",
      money(row.rate),
      row.qty,
      row.grpname       || "",
      row.categoryName  || "",
      money(row.discount),
      money(row.vat),
      money(row.total),
    ];

    buildTotalRow = () => {
      const t = list.reduce((acc, row) => {
        acc.discount += Number(row.discount || 0);
        acc.vat      += Number(row.vat      || 0);
        acc.total    += Number(row.total    || 0);
        return acc;
      }, { discount: 0, vat: 0, total: 0 });
      const row = Array(headers.length).fill("");
      row[0]  = "Grand Total:";
      row[10] = money(t.discount);
      row[11] = money(t.vat);
      row[12] = money(t.total);
      return row;
    };

  } else {
    // without item — summary
    headers = [
      "No.", "Voucher No", "Date", "Customer/Debtor",
      "Sub Total", "Discount", taxLabel,
      ...(showGst ? ["CGST", "SGST", "IGST"] : []),
      "Service", "Total",
    ];
    buildRow = (row, i) => [
      i + 1,
      row.vch_no   || "",
      fmtDate(row.date),
      row.customer || "",
      money(row.subtotal),
      money(row.discount),
      money(row.vat),
      ...(showGst ? [
        money(row.cgst_amount),
        money(row.sgst_amount),
        money(row.igst_amount),
      ] : []),
      money(row.service),
      money(row.total),
    ];

    buildTotalRow = () => {
      const t = list.reduce((acc, row) => {
        acc.subtotal += Number(row.subtotal     || 0);
        acc.discount += Number(row.discount     || 0);
        acc.vat      += Number(row.vat          || 0);
        acc.cgst     += Number(row.cgst_amount  || 0);
        acc.sgst     += Number(row.sgst_amount  || 0);
        acc.igst     += Number(row.igst_amount  || 0);
        acc.service  += Number(row.service      || 0);
        acc.total    += Number(row.total        || 0);
        return acc;
      }, { subtotal: 0, discount: 0, vat: 0, cgst: 0, sgst: 0, igst: 0, service: 0, total: 0 });

      return [
        "Grand Total:", "", "", "",
        money(t.subtotal),
        money(t.discount),
        money(t.vat),
        ...(showGst ? [money(t.cgst), money(t.sgst), money(t.igst)] : []),
        money(t.service),
        money(t.total),
      ];
    };
  }

  // ── Assemble rows ─────────────────────────────────────────────
  const rows = [
    [title],
    [],
    headers.map(escape),
    ...list.map((row, i) => buildRow(row, i).map(escape)),
    [],
    buildTotalRow().map(escape),
  ];

  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};